/* Qualité d'une photo client avant impression : trop sombre ou trop floue.
 *
 *   PhotoQuality.analyze(file, options?) → Promise<{ dark, blurry, brightness, sharpness }>
 *   PhotoQuality.message(result)      → texte d'alerte ('' si la photo est correcte)
 *   PhotoQuality.check(file, anchor)  → analyse puis affiche / retire l'alerte juste
 *                                       après `anchor` (un seul avertissement par anchor)
 *   PhotoQuality.checkMany(files, anchor, options?) → idem pour une grille (polaroids) : un seul
 *                                       message qui compte les photos concernées ;
 *                                       renvoie les résultats dans l'ordre des fichiers
 *   options.frame: 'polaroid' exclut les bordures blanches du fichier exporté.
 *   PhotoQuality.clear(anchor)        → retire l'alerte
 *
 * Analyse sur une réduction à 512 px (rapide, même échelle pour toutes les photos) :
 * luminosité = moyenne de la luminance ; netteté = variance du laplacien.
 * Seuils calés le 15/10/2026 sur une photo client imprimée beaucoup trop sombre
 * (tools/prerender/site/photo-quality.html pour les revérifier).
 * L'alerte n'empêche jamais de continuer : le client décide.
 */
(function () {
  'use strict';
  if (window.PhotoQuality) return;

  var SIZE = 512;
  var DARK_MEAN = 80;        // luminance moyenne (0–255) sous laquelle le tirage sort trop sombre
  var DARK_SHARE = 0.55;     // ou plus de 55 % des pixels sous 50
  var BLUR_VAR = 50;         // netteté (laplacien à contraste égal) sous laquelle la photo est floue

  function loadBitmap(file) {
    if (window.createImageBitmap) {
      return createImageBitmap(file).catch(function () { return loadImg(file); });
    }
    return loadImg(file);
  }

  function loadImg(file) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('image illisible')); };
      img.src = url;
    });
  }

  var cache = typeof WeakMap === 'function' ? new WeakMap() : null;
  var polaroidCache = typeof WeakMap === 'function' ? new WeakMap() : null;

  function analyze(file, options) {
    var framed = options && options.frame === 'polaroid';
    var results = framed ? polaroidCache : cache;
    if (results && results.has(file)) return results.get(file);
    var job = measure(file, framed);
    if (results) results.set(file, job);
    return job;
  }

  function measure(file, framed) {
    return loadBitmap(file).then(function (bmp) {
      // Le fichier Polaroid contient le papier blanc (photo-crop-modal).
      // Mesurer uniquement la fenêtre photo : le papier éclaircit artificiellement
      // la moyenne et ajoute des contours qui masquent aussi le flou.
      var sx = framed ? bmp.width * .065 : 0;
      var sy = framed ? bmp.width * .065 : 0;
      var w = framed ? bmp.width * .87 : bmp.width;
      var h = framed ? bmp.height - bmp.width * (.065 + .17) : bmp.height;
      var k = SIZE / Math.max(w, h);
      if (k > 1) k = 1;
      var cw = Math.max(1, Math.round(w * k)), ch = Math.max(1, Math.round(h * k));
      var canvas = document.createElement('canvas');
      canvas.width = cw; canvas.height = ch;
      var ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(bmp, sx, sy, w, h, 0, 0, cw, ch);
      if (bmp.close) bmp.close();
      var px = ctx.getImageData(0, 0, cw, ch).data;
      var n = cw * ch, lum = new Float32Array(n), sum = 0, darkPx = 0;
      for (var i = 0, j = 0; i < n; i++, j += 4) {
        var y = 0.2126 * px[j] + 0.7152 * px[j + 1] + 0.0722 * px[j + 2];
        lum[i] = y; sum += y;
        if (y < 50) darkPx++;
      }
      var mean = sum / n;
      var lv = 0;
      for (var q = 0; q < n; q++) { var d = lum[q] - mean; lv += d * d; }
      lv /= n;
      // Laplacien 4-voisins, variance.
      var ls = 0, ls2 = 0, m = 0;
      for (var yy = 1; yy < ch - 1; yy++) {
        for (var xx = 1; xx < cw - 1; xx++) {
          var p = yy * cw + xx;
          var l = lum[p - cw] + lum[p + cw] + lum[p - 1] + lum[p + 1] - 4 * lum[p];
          ls += l; ls2 += l * l; m++;
        }
      }
      // Netteté ramenée à un contraste commun (écart-type 50) : une photo sombre a des
      // écarts de luminance écrasés, sans être floue pour autant.
      var lapVar = m ? (ls2 / m - (ls / m) * (ls / m)) * 2500 / Math.max(lv, 100) : 0;
      var result = {
        brightness: Math.round(mean),
        darkShare: Math.round(darkPx / n * 100) / 100,
        sharpness: Math.round(lapVar),
        dark: mean < DARK_MEAN || darkPx / n > DARK_SHARE,
        blurry: lapVar < BLUR_VAR
      };
      return result;
    });
  }

  function message(r) {
    if (!r || (!r.dark && !r.blurry)) return '';
    if (r.dark && r.blurry) {
      return 'Cette photo est sombre et un peu floue : à l’impression, le résultat risque de ne pas être optimal. Si vous en avez une plus lumineuse et plus nette, n’hésitez pas à la choisir.';
    }
    if (r.dark) {
      return 'Cette photo est assez sombre : une fois imprimée, elle ressortira encore plus foncée. Si vous en avez une plus lumineuse, n’hésitez pas à la choisir.';
    }
    return 'Cette photo semble un peu floue : le tirage risque de manquer de netteté. Si vous en avez une plus nette, n’hésitez pas à la choisir.';
  }

  var CSS = '.photo-quality-warn{display:flex;gap:8px;align-items:flex-start;margin:10px 0 0;padding:10px 12px;' +
    'border-radius:12px;background:#FFF4E5;color:#7A4B00;font-size:12.5px;line-height:1.4;text-align:left}' +
    '.photo-quality-warn svg{flex:none;width:16px;height:16px;margin-top:1px}' +
    '.is-photo-quality-low{box-shadow:0 0 0 2px #E8920C !important}';
  var ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3 2 20h20L12 3z"/><path d="M12 10v4M12 17h.01"/></svg>';

  function ensureCss() {
    if (document.getElementById('photo-quality-css')) return;
    var st = document.createElement('style');
    st.id = 'photo-quality-css';
    st.textContent = CSS;
    document.head.appendChild(st);
  }

  // Une alerte par anchor, placée juste après lui ; la dernière analyse gagne.
  function check(file, anchor) {
    if (!anchor) return Promise.resolve(null);
    var token = {};
    anchor.__pqToken = token;
    return analyze(file).then(function (r) {
      if (anchor.__pqToken !== token) return r;
      setWarning(anchor, message(r));
      return r;
    }, function () { return null; });
  }

  function setWarning(anchor, text) {
    var el = anchor.__pqWarn;
    if (!text) {
      if (el && el.parentNode) el.parentNode.removeChild(el);
      anchor.__pqWarn = null;
      return;
    }
    ensureCss();
    if (!el) {
      el = document.createElement('div');
      el.className = 'photo-quality-warn';
      el.setAttribute('role', 'status');
      anchor.__pqWarn = el;
    }
    el.innerHTML = ICON + '<span></span>';
    el.lastChild.textContent = text;
    if (el.previousSibling !== anchor) anchor.parentNode.insertBefore(el, anchor.nextSibling);
  }

  function checkMany(files, anchor, options) {
    if (!anchor) return Promise.resolve([]);
    var token = {};
    anchor.__pqToken = token;
    files = (files || []).filter(function (f) { return f instanceof Blob; });
    return Promise.all(files.map(function (f) { return analyze(f, options).catch(function () { return null; }); })).then(function (rs) {
      if (anchor.__pqToken !== token) return rs;
      var bad = rs.filter(function (r) { return r && (r.dark || r.blurry); });
      var text = '';
      if (bad.length === 1 && files.length === 1) text = message(bad[0]);
      else if (bad.length) {
        var dark = bad.some(function (r) { return r.dark; }), blurry = bad.some(function (r) { return r.blurry; });
        var one = bad.length === 1;
        var what = dark && blurry ? (one ? 'sombre ou floue' : 'sombres ou floues') : dark ? (one ? 'sombre' : 'sombres') : (one ? 'floue' : 'floues');
        var better = dark && !blurry ? 'lumineuse' : blurry && !dark ? 'nette' : 'lumineuse et plus nette';
        text = one
          ? '1 photo semble ' + what + ' (signalée en orange) : à l’impression, le résultat risque de ne pas être optimal. Si vous en avez une plus ' + better + ', n’hésitez pas à la remplacer.'
          : bad.length + ' photos semblent ' + what + ' (signalées en orange) : à l’impression, le résultat risque de ne pas être optimal. Si vous en avez de plus ' + better.replace('lumineuse', 'lumineuses').replace('nette', 'nettes') + ', n’hésitez pas à les remplacer.';
      }
      setWarning(anchor, text);
      return rs;
    });
  }

  function clear(anchor) { if (anchor) { anchor.__pqToken = {}; setWarning(anchor, ''); } }

  window.PhotoQuality = { analyze: analyze, message: message, check: check, checkMany: checkMany, clear: clear,
    thresholds: { darkMean: DARK_MEAN, darkShare: DARK_SHARE, blurVar: BLUR_VAR } };
})();
